import React from 'react';
import styled from 'styled-components';
import { CargoOperationViewModel } from 'src/types/ViewModel/Stowage/CargoOperation/CargoOperationViewModel';
import {
    DefaultFlexStrechedWrapper,
    DefaultPanelWrapper,
    PanelSectionContent,
    PanelSubHeader,
} from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import OdfjellInputComponent from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { roundTo3Decimals } from 'src/utils/numberHelper';
import RotationDropDown from '../rotationDropDown/RotationDropDown';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';

const UnloadingCallsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

interface OperationSectionProps {
    cargoOperationVM: CargoOperationViewModel;
    isFieldReadOnly: (field: string) => boolean;
    onOperationInputChange: (changeObject: InputChangeData) => void;
    onSelectUnloadingPort: (portCall: RotationPortCall, berthCall: RotationBerthCall) => void;
    onSelectUnloadingPortForOperation: (operationGuid: string, portCall: RotationPortCall, berthCall: RotationBerthCall) => void;
    onClearUnloadingPort: () => void;
    getUnloadingCall: () => string;
    getSelectedItemForOperation: (operation: RotationCargoOperation) => string;
    unloadingOperations: RotationCargoOperation[];
    upcomingPortCalls: RotationPortCall[];
    isNewCargoOperation: boolean;
}

export const OperationSection: React.FC<OperationSectionProps> = ({
    cargoOperationVM,
    isFieldReadOnly,
    onOperationInputChange,
    onSelectUnloadingPort,
    onSelectUnloadingPortForOperation,
    onClearUnloadingPort,
    getUnloadingCall,
    getSelectedItemForOperation,
    unloadingOperations,
    upcomingPortCalls,
    isNewCargoOperation,
}) => {
    return (
        <PanelSectionContent>
            <PanelSubHeader>Operation</PanelSubHeader>
            <DefaultFlexStrechedWrapper alignTop noMargin className="highlight-panel">
                <DefaultPanelWrapper flexWidth="48%" alignTop noMargin>
                    <OdfjellInputComponent
                        isReadOnly={true}
                        type={'text'}
                        description={'Call'}
                        value={cargoOperationVM.berthCall?.portCall.portDisplayName + ' (' + cargoOperationVM.berthCall?.berthName + ')'}
                        name={'berthName'}
                        dataObject={cargoOperationVM}
                        leaveBlank={true}
                        disableUnit={true}
                        labelMinWidth={140}
                        textAlign="left"
                    />
                    <OdfjellInputComponent
                        isReadOnly={true}
                        type={'text'}
                        description={'Source'}
                        value={cargoOperationVM.dataSource}
                        name={'dataSource'}
                        dataObject={cargoOperationVM}
                        leaveBlank={true}
                        disableUnit={true}
                        labelMinWidth={140}
                        textAlign="left"
                    />
                    <OdfjellInputComponent
                        isReadOnly={true}
                        type={'text'}
                        description={'Type'}
                        value={cargoOperationVM.operationType}
                        disableUnit={true}
                        labelMinWidth={140}
                        textAlign="left"
                    />
                    {cargoOperationVM.operationType === 'Loading' && !isFieldReadOnly('unloadingBerthCall') && unloadingOperations.length <= 1 && (
                        <RotationDropDown
                            selectedItem={getUnloadingCall()}
                            upcomingPortCalls={upcomingPortCalls}
                            onCallChange={onSelectUnloadingPort}
                            onClearSelection={isNewCargoOperation ? onClearUnloadingPort : undefined}
                            tabIndex={5}
                        />
                    )}
                    {cargoOperationVM.operationType === 'Loading' && !isFieldReadOnly('unloadingBerthCall') && unloadingOperations.length > 1 && (
                        <UnloadingCallsContainer>
                            {unloadingOperations.map((operation, index) => (
                                <RotationDropDown
                                    key={operation.cargoOperationGuid}
                                    selectedItem={getSelectedItemForOperation(operation)}
                                    upcomingPortCalls={upcomingPortCalls}
                                    onCallChange={(portCall, berthCall) =>
                                        onSelectUnloadingPortForOperation(operation.cargoOperationGuid, portCall, berthCall)
                                    }
                                    onClearSelection={undefined} // Don't allow clearing when multiple operations exist
                                    tabIndex={5 + index}
                                />
                            ))}
                        </UnloadingCallsContainer>
                    )}
                    {cargoOperationVM.operationType === 'Loading' && isFieldReadOnly('unloadingBerthCall') && (
                        <>
                            {unloadingOperations.length <= 1 ? (
                                <OdfjellInputComponent
                                    isReadOnly={true}
                                    type={'text'}
                                    description={'Unload call'}
                                    value={getUnloadingCall()}
                                    disableUnit={true}
                                    labelMinWidth={140}
                                    textAlign="left"
                                />
                            ) : (
                                unloadingOperations.map((operation, index) => (
                                    <OdfjellInputComponent
                                        key={operation.cargoOperationGuid}
                                        isReadOnly={true}
                                        type={'text'}
                                        description={`Unload call ${index + 1}`}
                                        value={`${operation.berthCall.portCall.portDisplayName} - ${operation.berthCall.berthName}`}
                                        disableUnit={true}
                                        labelMinWidth={140}
                                        textAlign="left"
                                    />
                                ))
                            )}
                        </>
                    )}
                </DefaultPanelWrapper>
                <DefaultPanelWrapper flexWidth="48%" alignTop noMargin>
                    <OdfjellInputComponent
                        isReadOnly={isFieldReadOnly('Contract weight')}
                        type={'number'}
                        description={'Contract weight'}
                        value={roundTo3Decimals(cargoOperationVM.contractWeight).toFixed(3)}
                        name={'contractWeight'}
                        dataObject={cargoOperationVM}
                        unit={'MT'}
                        inputSize="large"
                        onChange={onOperationInputChange}
                        tabIndex={6}
                        maxDecimals={3}
                    />
                    <OdfjellInputComponent
                        isReadOnly={isFieldReadOnly('Nominated weight')}
                        type={'number'}
                        description={'Nominated weight'}
                        value={roundTo3Decimals(cargoOperationVM.nominatedWeight).toFixed(3)}
                        name={'nominatedWeight'}
                        dataObject={cargoOperationVM}
                        leaveBlank={true}
                        unit={'MT'}
                        inputSize="large"
                        onChange={onOperationInputChange}
                        tabIndex={7}
                        maxDecimals={3}
                    />
                    <OdfjellInputComponent
                        isReadOnly={isFieldReadOnly('Nominated volume')}
                        type={'number'}
                        description={'Nominated volume'}
                        value={roundTo3Decimals(Number(cargoOperationVM.nominatedVolume)).toFixed(3)}
                        name={'nominatedVolume'}
                        dataObject={cargoOperationVM}
                        leaveBlank={true}
                        unit={'m³'}
                        inputSize="large"
                        onChange={onOperationInputChange}
                        tabIndex={8}
                        maxDecimals={3}
                    />
                    <OdfjellInputComponent
                        isReadOnly={true}
                        type={'number'}
                        description={'Bill of lading'}
                        value={cargoOperationVM.billOfLadingWeight ? roundTo3Decimals(Number(cargoOperationVM.billOfLadingWeight)).toFixed(3) : 'N/A'}
                        name={'billOfLadingWeight'}
                        dataObject={cargoOperationVM}
                        leaveBlank={true}
                        unit={'MT'}
                        inputSize="large"
                    />
                </DefaultPanelWrapper>
            </DefaultFlexStrechedWrapper>
        </PanelSectionContent>
    );
};
